import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  FileCheck,
  ShieldCheck,
  ClipboardList,
  Settings,
  Key,
  Menu,
  X,
  ChevronRight,
  LogOut,
  Home,
  Keyboard,
  Puzzle,
} from "lucide-react";
import { useState } from "react";
import { useAuthStore, PLAN_LIMITS } from "../stores/auth";
import { Footer } from "./Footer";
import { KeyboardShortcutsModal } from "./KeyboardShortcutsModal";
import { useGlobalShortcuts } from "../hooks/useKeyboardShortcuts";

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Plugin Studio", href: "/studio", icon: Puzzle },
  { name: "Credentials", href: "/credentials", icon: FileCheck },
  { name: "Verify", href: "/verify", icon: ShieldCheck },
  { name: "Audit Log", href: "/audit", icon: ClipboardList },
  { name: "API Keys", href: "/api-keys", icon: Key },
  { name: "Settings", href: "/settings", icon: Settings },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant, tenantName, logout } = useAuthStore();

  // Enable global keyboard shortcuts
  useGlobalShortcuts();

  // Note: Authentication is handled by AuthGuard wrapper in App.tsx

  // Get breadcrumb from current path
  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/credentials/issue") return "Credentials / Issue New";
    const nav = navigation.find((n) => n.href === path);
    return nav?.name || "Page";
  };

  // Calculate usage percentage
  const getUsageInfo = () => {
    const plan = tenant?.plan || "starter";
    const limits = PLAN_LIMITS[plan as keyof typeof PLAN_LIMITS];
    const used = tenant?.usage?.verifications || 0;
    const max = limits.verifications;
    const percentage = max === Infinity ? 0 : Math.min((used / max) * 100, 100);
    
    return {
      used,
      max: max === Infinity ? "∞" : max.toLocaleString(),
      percentage,
      plan: plan.charAt(0).toUpperCase() + plan.slice(1),
    };
  };

  const usageInfo = getUsageInfo();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:relative lg:flex lg:flex-col
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg trust-gradient flex items-center justify-center">
              <span className="text-white font-bold text-sm">ZK</span>
            </div>
            <span className="font-semibold text-slate-900">ZKCN Portal</span>
          </div>
          <button
            className="lg:hidden p-1 rounded-md hover:bg-slate-100"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/" && location.pathname.startsWith(item.href));
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-colors duration-150
                  ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon
                  className={`w-5 h-5 ${isActive ? "text-primary-500" : "text-slate-400"}`}
                />
                {item.name}
              </NavLink>
            );
          })}

          {/* Keyboard shortcuts hint */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("show-keyboard-shortcuts"))}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150 mt-2"
          >
            <Keyboard className="w-5 h-5 text-slate-400" />
            <span>Shortcuts</span>
            <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-slate-100 border border-slate-200 rounded text-slate-500">?</kbd>
          </button>
        </nav>

        {/* Plan indicator */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="p-3 rounded-lg trust-gradient-subtle">
            <p className="text-xs font-medium text-slate-600">Current Plan</p>
            <p className="text-sm font-semibold text-primary-700">{usageInfo.plan}</p>
            <p className="text-xs text-slate-500 mt-1">
              {usageInfo.used.toLocaleString()} / {usageInfo.max} verifications
            </p>
            <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-success-500 rounded-full transition-all duration-300"
                style={{ width: `${usageInfo.percentage}%` }}
              />
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu className="w-5 h-5 text-slate-500" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center text-sm">
              <span className="text-slate-400">Enterprise Portal</span>
              <ChevronRight className="w-4 h-4 mx-2 text-slate-300" />
              <span className="font-medium text-slate-700">
                {getBreadcrumb()}
              </span>
            </div>
          </div>

          {/* User menu */}
          <div className="flex items-center gap-3">
            {/* Back to site link */}
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              title="Go to main website"
            >
              <Home className="w-4 h-4" />
              <span>Back to Site</span>
            </Link>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-slate-700">{tenantName || "Organization"}</p>
              <p className="text-xs text-slate-500">{tenant?.email || "admin@organization.com"}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-success-400 flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {(tenantName || "O").slice(0, 2).toUpperCase()}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <Footer variant="compact" />
      </div>

      {/* Keyboard shortcuts modal */}
      <KeyboardShortcutsModal />
    </div>
  );
}
